// Role definitions
export const ROLES = {
  SUPER_ADMIN: 'SuperAdmin',
  CEO: 'CEO',
  EXECUTIVE_TEAM: 'ExecutiveTeam',
  TASK_FORCE: 'TaskForce',
  POINT_PEOPLE: 'PointPeople',
  TEAM_LEAD_DOCTOR: 'TeamLeadDoctor',
  TEAM_LEAD_OPTICAL: 'TeamLeadOptical',
  NEXT_GEN_EXECUTIVE_TEAM: 'NextGenExecutiveTeam',
  TRAINING_LEVEL: 'TrainingLevel',
  TEAM_LEAD: 'TeamLead',
  TEAM_MEMBER: 'TeamMember',
  VIRTUAL_EXECUTIVE: 'VirtualExecutive',
  EXECUTIVE_VIRTUAL_TEAM: 'ExecutiveVirtualTeam',
  VIRTUAL_TEAM_MEMBER: 'VirtualTeamMember',
  BILLING_AUDITING: 'BillingAuditing',
  BOLT_TASKFORCE: 'TaskForce',
  TRAINER: 'Trainer'
};

// Role hierarchy for access control
export const ROLE_HIERARCHY = {
  [ROLES.SUPER_ADMIN]: 13,
  [ROLES.CEO]: 12,
  [ROLES.EXECUTIVE_TEAM]: 11,
  [ROLES.TASK_FORCE]: 10,
  [ROLES.POINT_PEOPLE]: 9,
  [ROLES.TEAM_LEAD_DOCTOR]: 8,
  [ROLES.TEAM_LEAD_OPTICAL]: 7,
  [ROLES.TEAM_MEMBER]: 4,
};

// Meeting types
export const MEETING_TYPES = {
  LEVEL_10: 'Level10',
  QUARTERLY: 'Quarterly',
  ANNUAL: 'Annual',
  DAILY_HUDDLE: 'DailyHuddle'
};

// Status indicators
export const STATUS_TYPES = {
  GREEN: 'green',
  YELLOW: 'yellow',
  RED: 'red'
};

// KPI categories
export const KPI_CATEGORIES = {
  REVENUE: 'revenue',
  PATIENTS: 'patients',
  NEW_CLIENTS: 'newClients',
  RETENTION: 'retention',
  SATISFACTION: 'satisfaction',
  OPERATIONS: 'operations',
  MARKETING: 'marketing',
  FINANCE: 'finance'
};

// Template categories
export const TEMPLATE_CATEGORIES = {
  MEETING: 'meeting',
  PAGE: 'page',
  SCORECARD: 'scorecard',
  COACHING: 'coaching',
  ONBOARDING: 'onboarding'
};

// Task statuses
export const TASK_STATUSES = {
  TODO: 'todo',
  IN_PROGRESS: 'inProgress',
  COMPLETED: 'completed'
};

// Priority levels
export const PRIORITY_LEVELS = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high'
};

// Date formats
export const DATE_FORMATS = {
  DISPLAY_DATE: 'MMM dd, yyyy',
  DISPLAY_DATE_TIME: 'MMM dd, yyyy h:mm a',
  ISO_DATE: 'yyyy-MM-dd',
  ISO_DATE_TIME: 'yyyy-MM-dd\'T\'HH:mm:ss'
};

// AI Assistant prompt templates
export const AI_PROMPTS = {
  MEETING_SUMMARY: 
    `Summarize the following {meetingType} Meeting transcript from Chime Note. 
     Highlight all rocks reviewed, issues identified and solved (IDS), any incomplete to-dos, 
     and the final meeting score. Return the summary in markdown format categorized by agenda section.`,
  
  COACHING_ASSISTANCE: 
    `Based on the following scorecard history and recent coaching notes for employee {employeeName}, 
     generate a summary of performance trends and provide three coaching suggestions tailored to their goals. 
     Include a motivational comment to help them improve over the next 30 days.`,
  
  DAILY_HUDDLE_RECAP: 
    `Given this huddle report data from {location} on {date}, summarize the top achievements, 
     shortfalls, and open opportunities. Recommend one short-term goal for the team to address tomorrow.`
};

// Navigation sections
export const NAV_SECTIONS = {
  MAIN: 'main',
  MANAGEMENT: 'success',
  SUCCESS: 'success',
  SERVICES: 'services',
  PERSONAL: 'personal',
  SETTINGS: 'settings'
};

// Navigation items
export const NAV_ITEMS = {
  WORKSPACE: {
    name: 'My Workspace (Coming Soon)',
    path: '/workspace',
    icon: 'briefcase-outline',
    section: NAV_SECTIONS.PERSONAL,
    order: 10
  },
  INBOX: {
    name: 'My Inbox (Coming Soon)',
    path: '/inbox',
    icon: 'inbox',
    section: NAV_SECTIONS.PERSONAL,
    order: 20
  },
  AGENDA: {
    name: 'My Agenda (Coming Soon)',
    path: '/agenda',
    icon: 'calendar-check',
    section: NAV_SECTIONS.PERSONAL,
    order: 30
  },
  FAVORITES: {
    name: 'Favorites (Coming Soon)',
    path: '/favorites',
    icon: 'star-outline',
    section: 'removed',
    order: 35
  },
  DIRECT_MESSAGES: {
    name: 'Direct Messages (Coming Soon)',
    path: '/dm',
    icon: 'message-text-outline',
    section: NAV_SECTIONS.PERSONAL,
    order: 40
  },
  CALLS: {
    name: 'Calls (Coming Soon)',
    path: '/calls',
    icon: 'phone-outline',
    section: NAV_SECTIONS.PERSONAL,
    order: 50
  },
  BOLT_AI: {
    name: 'Bolt AI (Coming Soon)',
    path: '/ai',
    icon: 'robot-outline',
    section: NAV_SECTIONS.PERSONAL,
    order: 55
  },
  DASHBOARD: {
    name: 'Dashboard',
    path: '/',
    icon: 'view-dashboard',
    section: NAV_SECTIONS.MAIN
  },
  HEADLINES: {
    name: 'Headlines',
    path: '/headlines',
    icon: 'text-box-multiple-outline',
    section: NAV_SECTIONS.MAIN
  },
  PAGES: {
    name: 'Folders (Removed)',
    path: '/pages',
    icon: 'file-document-outline',
    section: 'removed'
  },
  LAB_TRACKER: {
    name: 'Lab Tracker (Coming Soon)',
    path: '/lab',
    icon: 'flask-outline',
    section: 'removed'
  },
  INVENTORY_MAIN: {
    name: 'Inventory (Coming Soon)',
    path: '/inventory',
    icon: 'warehouse',
    section: 'removed'
  },
  STICKYSTACK: {
    name: 'StickyStack (Coming Soon)',
    path: '/stickystack',
    icon: 'view-column',
    section: NAV_SECTIONS.MAIN
  },
  CALENDARS: {
    name: 'Calendars',
    path: '/calendars',
    icon: 'calendar',
    section: NAV_SECTIONS.MAIN
  },
  MEETING_AGENDAS: {
    name: 'Meeting Agendas',
    path: '/meeting-agendas',
    icon: 'clipboard-text-outline',
    section: NAV_SECTIONS.MAIN
  },
  PASSWORDS: {
    name: 'Passwords (Coming Soon)',
    path: '/passwords',
    icon: 'key-variant',
    section: 'removed'
  },
  SHOPPING_LIST: {
    name: 'Shopping List (Coming Soon)',
    path: '/shopping-list',
    icon: 'cart-outline',
    section: 'removed'
  },
  IMPORTANT_CONTACTS: {
    name: 'Important Contacts (Coming Soon)',
    path: '/contacts',
    icon: 'account-box-outline',
    section: 'removed'
  },
  REFERRALS: {
    name: 'Referral Tracking (Coming Soon)',
    path: '/referrals',
    icon: 'account-arrow-right-outline',
    section: 'removed'
  },
  PERFORMANCE: {
    name: 'Performance',
    path: '/performance',
    icon: 'chart-line',
    section: NAV_SECTIONS.MANAGEMENT
  },
  TEAM: {
    name: 'Team',
    path: '/team',
    icon: 'account-group',
    section: 'removed'
  },
  STAFFING: {
    name: 'Staffing & Doctor Scheduling (Coming Soon)',
    path: '/staffing',
    icon: 'calendar-account',
    section: NAV_SECTIONS.MANAGEMENT
  },
  COACHING: {
    name: 'Coaching',
    path: '/coaching',
    icon: 'clipboard-account',
    section: NAV_SECTIONS.MANAGEMENT
  },
  BUTTONS: {
    name: 'Button your Buttons ( Coming Soon )',
    path: '/button-your-buttons',
    icon: 'gesture-tap-button',
    section: NAV_SECTIONS.MANAGEMENT
  },
  LEADERSHIP_AI: {
    name: 'Nextgen AI Leadership Assistance (Coming Soon)',
    path: '/leadership-ai',
    icon: 'brain',
    section: NAV_SECTIONS.MANAGEMENT
  },
  TEMPLATES: {
    name: 'Templates',
    path: '/templates',
    icon: 'file-table-box-multiple',
    section: 'removed'
  },
  NPS: {
    name: 'Net Promoter Score',
    path: '/nps',
    icon: 'star-circle-outline',
    section: NAV_SECTIONS.MANAGEMENT
  },
  NPS_SURVEY_CALL: {
    name: 'NPS Survey Call',
    path: '/nps-survey-call',
    icon: 'phone-check-outline',
    section: NAV_SECTIONS.MANAGEMENT
  },
  DICTATION: {
    name: 'Doctor Dictation (Coming Soon)',
    path: '/dictation',
    icon: 'microphone-outline',
    section: NAV_SECTIONS.SERVICES
  },
  UTILIZATION: {
    name: 'Percent Utilization (Coming Soon)',
    path: '/utilization',
    icon: 'percent-outline',
    section: NAV_SECTIONS.MANAGEMENT
  },
  RFID_SECURITY: {
    name: 'RFID Inventory/Security (Coming Soon)',
    path: '/rfid',
    icon: 'shield-lock',
    section: NAV_SECTIONS.SERVICES
  },
  TALENT: {
    name: 'Talent Pipeline (Coming Soon)',
    path: '/talent',
    icon: 'account-search-outline',
    section: NAV_SECTIONS.SERVICES
  },
  FINANCING: {
    name: 'Patient Financing (Coming Soon)',
    path: '/financing',
    icon: 'cash-multiple',
    section: NAV_SECTIONS.SERVICES
  },
  ODOO: {
    name: 'Odoo Accounting (Coming Soon)',
    path: '/odoo',
    icon: 'book-open-variant',
    section: NAV_SECTIONS.SERVICES
  },
  AUDIT: {
    name: 'Audit (Coming Soon)',
    path: '/audit',
    icon: 'shield-search',
    section: NAV_SECTIONS.SERVICES
  },
  BILLING: {
    name: 'Billing (Coming Soon)',
    path: '/billing',
    icon: 'receipt-text-outline',
    section: NAV_SECTIONS.SERVICES
  },
  CALL_CENTER: {
    name: 'Call Center (Coming Soon)',
    path: '/call-center',
    icon: 'headset',
    section: NAV_SECTIONS.SERVICES
  },
  MARKETING: {
    name: 'Social/Marketing Studio (Coming Soon)',
    path: '/marketing',
    icon: 'bullhorn-outline',
    section: NAV_SECTIONS.SERVICES
  },
  TRAINING_ACADEMY: {
    name: 'Training Academy (Coming Soon)',
    path: '/training-academy',
    icon: 'school-outline',
    section: NAV_SECTIONS.SERVICES
  },
  SETTINGS: {
    name: 'Settings',
    path: '/settings',
    icon: 'cog',
    section: NAV_SECTIONS.SETTINGS
  },
  HELP: {
    name: 'Help Desk',
    path: '/help',
    icon: 'help-circle',
    section: 'removed',
    order: 35
  }
};
